const qoreIdScriptUrl = String(
    (document.querySelector('script[src*="/qoreid-sdk/qoreid.js"]') as any)
        ?.src || ""
);

import { Config } from "../../Config";
import {
    FlowRequest,
    IApplicationController,
    SdkElementData,
    SdkInitializationData,
    SdkProduct,
    OptionDataOptions,
    IQoreIdButton,
    IBrowserEventData,
    BrowserEventData,
    IEjs,
    OCRScanData
} from "../interfaces";
import "../../styles/sdk.scss";
import { PROD_KEYWORD_FACE_VERIFICATION } from "../constants";
import { SampleAppUtilities } from "../utilities/SampleAppUtilities";
import { FaceTecSDK } from "../../core-sdk/FaceTecSDK.js/FaceTecSDK";
import { AdditionalScreens } from "../utilities/AdditionalScreens";
import {
    FaceTecIDScanResult,
    FaceTecSessionResult
} from "../../core-sdk/FaceTecSDK.js/FaceTecPublicApi";
import { CollectionProcessor } from "../processors/collection/CollectionProcessor";
import { removeUnneeded } from "../utilities/utils";
import { PHONE_CODES } from "../../../src/constants/phoneCodes";

type WindowWithQoreIdSdk = Window &
    typeof globalThis & { QoreIDWebSdk: BaseController } & {
        QoreIDFaceTecSDKInitialized: boolean;
    };
declare const window: WindowWithQoreIdSdk;

declare let ejs: IEjs;

const sdkRequestBodyEjs: string = require("../../templates/sdkRequestBody.ejs");
const sdkFaceTecInstructions: string = require("../../templates/sdkFaceTecInstructions.ejs");

export class BaseController implements IApplicationController {
    controllerType = "base";
    public inIFrame = window.self !== window.top;
    redirectingData: IBrowserEventData = null as any;

    mainElement: HTMLElement = null as any;
    sdkRequestBodyHolderElement: HTMLElement = null as any;
    sdkBodyElement: HTMLElement = null as any;
    sdkWaiterElement: HTMLElement = null as any;
    sdkStatusElement: HTMLElement = null as any;

    finalSubmitButton: HTMLButtonElement = null as any;

    sdkElementData: SdkElementData = {} as any;
    sdkInitializationData: SdkInitializationData = null as any;

    flowRequest: FlowRequest = null as any;

    currentFlowStepIndex: number = null as any;

    latestEnrollmentIdentifier = "";
    latestSessionResult? = null as any;
    latestIDScanResult? = null as any;

    activeElement: HTMLElement = null as any;
    video: HTMLVideoElement = null as any;
    takePhotoBtn: HTMLButtonElement = null as any;
    actionBtns: HTMLDivElement = null as any;
    capturedImage: HTMLImageElement = null as any;

    initialized = false;
    sdkVersion = "0.0.1";
    sdkSource = "web_sdk";
    isHybridOrchestrated = false;

    isLoading = false;
    appendedScript = false;

    faceTecSDKInitialized = window.QoreIDFaceTecSDKInitialized || false;

    lastSumbittedFormData = null as any;
    logId?: number;
    currentProductCode = "";

    allowedWhiteLabel = true;

    constraints = {
        video: {
            width: { min: 240, ideal: 480, max: 640 },
            height: { min: 160, ideal: 680, max: 720 },
            facingMode: 'user',
        },
    };

    constructor() {
        try {
            var testBrowserSupport = new Function(
                "'use strict'; const testConstSupport = 0; var testWorkerSupport = Worker.length; var testWasmSupport = WebAssembly.Module; const d = new Document();"
            );
            testBrowserSupport();
        } catch (e) {
            // browser doesn't not support const/workers/wasm - cannot run FaceTecSDK
            // call incompatible browser handler here
            alert(
                "Browser not supported: missing basic JS constructs required for QoreID.js to load.\n\n" +
                navigator.userAgent
            );
            window.location.href = window.location.origin;
            return;
        }
        this.initiateWebLibrary();
    }

    getEjs(): IEjs {
        return ejs;
    }

    getBrowserFingerPrint(): string {
        let browserFingerPrint =
            localStorage.getItem("QoreIDBrowserFingerPrint") || "";
        if (!browserFingerPrint) {
            browserFingerPrint =
                "QoreID_WebSDK_" + SampleAppUtilities.generateUUId();
            localStorage.setItem(
                "QoreIDBrowserFingerPrint",
                browserFingerPrint
            );
        }

        return browserFingerPrint;
    }

    getLatestEnrollmentIdentifier(): string {
        return this.latestEnrollmentIdentifier;
    }
    clearLatestEnrollmentIdentifier(): string {
        this.latestEnrollmentIdentifier = "";
        return this.latestEnrollmentIdentifier;
    }

    getScriptAsHostDomain(): true | URL {
        const scriptUrlData = new URL(qoreIdScriptUrl);
        return scriptUrlData.host === window.location.host
            ? true
            : scriptUrlData;
    }

    initiateWebLibrary() {
        let ejsScript: HTMLScriptElement;

        if (typeof ejs == "undefined") {
            ejsScript = document.createElement("script");
            ejsScript.setAttribute(
                "src",
                "https://github.com/mde/ejs/releases/download/v3.1.8/ejs.min.js"
            );
            ejsScript.async = false;
            if (document.body) {
                this.appendedScript = true;
                document.body.appendChild(ejsScript);
            }
        }

        // get token from
        // window.addEventListener("load", () => {
        //     if (!this.appendedScript) document.body.appendChild(ejsScript);
        //     this.prepareStartButton();
        // });

        this.prepareStartButton();
    }

    async prepareStartButton(hybridAutoLaunch = true) {
        this.sdkElementData = {} as any;
        console.log("Analyzing QoreIDButton element....");
        this.mainElement = document.getElementById(
            "QoreIDButton"
        ) as HTMLElement;
        if (!this.mainElement) {
            console.error("QoreID button is missing on this page");
            return;
        }

        let stringifiedJson = "";

        const qoreIdSdkPostedData: IQoreIdButton = (window as any)
            .QoreIdSdkPostedData;

        // parsing initial title
        this.sdkElementData.hideButton =
            String(
                qoreIdSdkPostedData
                    ? qoreIdSdkPostedData.title
                    : this.mainElement.getAttribute("hideButton")
            )
                .trim()
                .toLowerCase() || "no";

        // parsing initial title
        this.sdkElementData.initalTitle =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.title
                : this.mainElement.getAttribute("title")) || "Verify";

        // parsing initial title
        this.sdkSource =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.sdkSource
                : this.mainElement.getAttribute("sdkSource")) || this.sdkSource;
        this.isHybridOrchestrated = this.sdkSource.toLowerCase() !== "web_sdk";

        // parsing client id
        this.sdkElementData.clientId =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.clientId
                : this.mainElement.getAttribute("clientId")) || "";
        if (!this.sdkElementData.clientId) {
            console.error("Client ID is missing");
            return;
        }

        // parsing flowId
        this.sdkElementData.flowId = Number(
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.flowId
                : this.mainElement.getAttribute("flowId")) || 0
        );

        // parsing previousIdentityVerificationId
        this.sdkElementData.previousIdentityVerificationId = Number(
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.previousIdentityVerificationId
                : this.mainElement.getAttribute("previousIdentityVerificationId")) || 0
        );

        // parsing client secret
        this.sdkElementData.productCode =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.productCode
                : this.mainElement.getAttribute("productCode")) || "";
        if (!this.sdkElementData.flowId && !this.sdkElementData.productCode) {
            console.error("ProductCode is missing");
            return;
        }

        if (
            this.sdkElementData.productCode &&
            String(this.sdkElementData.productCode).indexOf("verifind") >= 0
        ) {
            console.error("Invalid productCode");
            return;
        }

        // parsing customerReference
        this.sdkElementData.customerReference =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.customerReference
                : this.mainElement.getAttribute("customerReference")) || "";

        // parsing applicant data
        stringifiedJson =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.applicantData
                : this.mainElement.getAttribute("applicantData")) || "";
        try {
            this.sdkElementData.applicantData =
                (stringifiedJson && JSON.parse(stringifiedJson)) || null;
            if (!this.sdkElementData.applicantData) {
                console.error("Invalid applicant data");
                return;
            } else {
                removeUnneeded(this.sdkElementData.applicantData);
            }
        } catch (error) {
            console.error("Invalid applicant data");
            return;
        }

        // parsing identity data
        stringifiedJson =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.identityData
                : this.mainElement.getAttribute("identityData")) || "";
        try {
            this.sdkElementData.identityData =
                (stringifiedJson && JSON.parse(stringifiedJson)) || null;
            if (this.sdkElementData.identityData) {
                removeUnneeded(this.sdkElementData.identityData);
            }
        } catch (error) {
            console.error("Invalid applicant data");
            return;
        }

        // parsing address data
        stringifiedJson =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.addressData
                : this.mainElement.getAttribute("addressData")) || "";
        try {
            this.sdkElementData.addressData =
                (stringifiedJson && JSON.parse(stringifiedJson)) || null;
            if (this.sdkElementData.addressData) {
                removeUnneeded(this.sdkElementData.addressData);
            }
        } catch (error) {
            console.error("Invalid address data");
            return;
        }

        // organisation extra data
        stringifiedJson =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.extraData
                : this.mainElement.getAttribute("extraData")) || "";


        try {
            if (String(stringifiedJson).trim() == '{}' || String(stringifiedJson).trim() === "undefined") stringifiedJson = "";
            this.sdkElementData.extraData =
                (stringifiedJson && JSON.parse(stringifiedJson)) || undefined;
            if (this.sdkElementData.extraData) {
                removeUnneeded(this.sdkElementData.extraData);
            }
        } catch (error) {
            console.error("Invalid organisation extra data: ", error);
            return;
        }

        // parsing ocrAcceptedDocuments
        stringifiedJson =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.ocrAcceptedDocuments
                : this.mainElement.getAttribute("ocrAcceptedDocuments")) || "";
        try {
            this.sdkElementData.ocrAcceptedDocuments = stringifiedJson;
        } catch (error) {
            console.error("Invalid intending step numbers");
            return;
        }

        // parsing address data
        const intializedTrigger =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.onQoreIDSdkInitialized
                : this.mainElement.getAttribute("onQoreIDSdkInitialized")) ||
            "";
        const submittedTrigger =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.onQoreIDSdkSubmitted
                : this.mainElement.getAttribute("onQoreIDSdkSubmitted")) || "";
        const errorTrigger =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.onQoreIDSdkError
                : this.mainElement.getAttribute("onQoreIDSdkError")) || "";
        const closedTrigger =
            (qoreIdSdkPostedData
                ? qoreIdSdkPostedData.onQoreIDSdkClosed
                : this.mainElement.getAttribute("onQoreIDSdkClosed")) || "";

        try {
            this.sdkElementData.initializedEventTrigger =
                (intializedTrigger && eval(`(${intializedTrigger})`)) ||
                ((_: Event) => { });
            this.sdkElementData.submittedEventTrigger =
                (submittedTrigger && eval(`(${submittedTrigger})`)) ||
                ((_: Event) => { });
            this.sdkElementData.errorEventTrigger =
                (errorTrigger && eval(`(${errorTrigger})`)) ||
                ((_: Event) => { });
            this.sdkElementData.closedEventTrigger =
                (closedTrigger && eval(`(${closedTrigger})`)) ||
                ((_: Event) => { });

            if (
                typeof this.sdkElementData.initializedEventTrigger !==
                "function" ||
                typeof this.sdkElementData.submittedEventTrigger !==
                "function" ||
                typeof this.sdkElementData.errorEventTrigger !== "function" ||
                typeof this.sdkElementData.closedEventTrigger !== "function"
            )
                throw new Error("invalid event handler function..");
        } catch (error: any) {
            console.error(error.message);
            return;
        }

        // start view
        this.mainElement.innerHTML = `
            <div id="sdkRequestBodyHolder">
                <div id="sdkRequestBody">
                    ${this.isHybridOrchestrated ||
                this.sdkElementData.hideButton === "yes" ||
                qoreIdSdkPostedData
                ? ""
                : `<button class="sdkButton" onclick="QoreIDWebSdk.initialize(QoreIDWebSdk)">${this.sdkElementData.initalTitle}</button>`
            }
                </div>
                <div id="sdkRequestWaiter" style="display: none">
                    <div id="sdkFormCloseButton" onclick="QoreIDWebSdk.closeVerification()">x</div>
                    <div id="sdkRequestLoader"></div>
                    <div id="sdkRequestPleaseWait">please wait...</div>
                </div>
                <div id="sdkRequestStatus">
                </div>

                <div id="sdkPoweredByHolder">
                    <p id="sdkPoweredByText" style="display: none">powered by <span class="qoreid-link">QoreID</span></p>
                </div>
            </div>`;

        this.sdkStatusElement = document.getElementById(
            "sdkRequestStatus"
        ) as HTMLElement;
        this.sdkBodyElement = document.getElementById(
            "sdkRequestBody"
        ) as HTMLElement;
        this.sdkWaiterElement = document.getElementById(
            "sdkRequestWaiter"
        ) as HTMLElement;
        this.sdkRequestBodyHolderElement = document.getElementById(
            "sdkRequestBodyHolder"
        ) as HTMLElement;

        if (
            (this.isHybridOrchestrated && hybridAutoLaunch) ||
            qoreIdSdkPostedData
        ) {
            this.initialize(this as any);
        }
    }

    start() {
        this.initialize(this);
    }

    apiRequest<T extends any>(
        path: string,
        method = "GET",
        data: any = undefined,
        isJsonData = false,
        token = ""
    ): Promise<T> {
        return new Promise((resolve, reject) => {
            try {
                let xhr = new XMLHttpRequest();
                xhr.open(method, path);
                if (isJsonData)
                    xhr.setRequestHeader("Content-Type", "application/json");
                if (token)
                    xhr.setRequestHeader("Authorization", "Bearer " + token);

                xhr.onreadystatechange = () => {
                    if (xhr.readyState === XMLHttpRequest.DONE) {
                        // The request is done; did it work?
                        // ***Yes, use `xhr.responseText` here***
                        try {
                            const response = isJsonData
                                ? JSON.parse(xhr.responseText)
                                : xhr.responseText;
                            if (xhr.status >= 200 && xhr.status < 400)
                                return resolve(response);
                            else {
                                // ***No, tell the callback the call failed***
                                if (response?.devMessage)
                                    console.warn(response?.devMessage);
                                const error = new Error(
                                    response?.message || response
                                );
                                (error as any).code = xhr.status;
                                (error as any).data = response;
                                return reject(error);
                            }
                        } catch (error) {
                            if (xhr.status >= 200 && xhr.status < 400)
                                return resolve(xhr.responseText as any);
                            else {
                                // ***No, tell the callback the call failed***
                                const error = new Error(xhr.responseText);
                                (error as any).code = xhr.status;
                                return reject(error);
                            }
                        }
                    }
                };
                xhr.onerror = (e) => reject(e);
                xhr.send(data ? JSON.stringify(data) : undefined);
            } catch (error) {
                return reject(error);
            }
        });
    }

    async getSdkInitializationData(
        clientId: string,
        workflowId: number
    ): Promise<SdkInitializationData> {
        return this.apiRequest<SdkInitializationData>(
            Config.ApiBaseUrl + "/sdk/initialize",
            "POST",
            { clientId, source: "web_sdk", workflowId },
            true
        )
            .then((data) => {
                this.allowedWhiteLabel =
                    data.environment?.allowedWhiteLabel ?? true;
                Config.allowedWhiteLabel = this.allowedWhiteLabel;
                return data;
            })
            .catch((error: Error) => {
                this.setSdkStatusElement(
                    `<div class="sdkErrorMessage">${error.message || "Initialization failed..."
                    }</div>`
                );
                console.error(error);
                return null as any;
            });
    }

    initialize(self: IApplicationController) {
        this.logId = undefined;
        (self as any).logId = undefined;

        self.initialized = true;
        self.startProcessing();
        self.lastSumbittedFormData = null as any;
    }

    setSdkBodyElement(innerHTML: string) {
        if (this.sdkBodyElement) this.sdkBodyElement.innerHTML = innerHTML;
    }

    setSdkStatusElement(innerHTML: string) {
        if (this.sdkStatusElement) this.sdkStatusElement.innerHTML = innerHTML;
        if (innerHTML && this.sdkRequestBodyHolderElement) {
            this.sdkRequestBodyHolderElement.scrollTop =
                this.sdkRequestBodyHolderElement.scrollHeight;
        }
    }

    private startProcessingIFrame(scriptDomainData: URL) {
        this.mainElement.classList.add("sdkPopup", "sdkIFrameHolder");
        const payload = {
            ...this.sdkElementData,
            onQoreIDSdkInitialized: undefined,
            onQoreIDSdkSubmitted: undefined,
            onQoreIDSdkError: undefined,
            onQoreIDSdkClosed: undefined
        };

        const redirectUrl = new URL(`${scriptDomainData.origin}/sdk/qoreid`);

        for (const key in payload) {
            let value = (payload as any)[key];
            if (typeof value !== "string") value = JSON.stringify(value);
            redirectUrl.searchParams.append(key, value);
        }

        const innerIFrame = document.createElement("iframe");
        innerIFrame.allowFullscreen = true;
        innerIFrame.allow =
            "geolocation; microphone; camera; midi; encrypted-media;";
        innerIFrame.setAttribute("frameborder", "0");
        innerIFrame.setAttribute("allowtransparency", "true");
        innerIFrame.style.width = "100%";
        innerIFrame.style.height = "100%";
        innerIFrame.id = "qoreIdInnerIFrame";
        innerIFrame.src = redirectUrl.href;
        this.mainElement.innerHTML = innerIFrame.outerHTML;
    }

    async startProcessing() {
        // If script and host not same domain
        const scriptDomainData = this.getScriptAsHostDomain();
        if (scriptDomainData instanceof URL) {
            this.startProcessingIFrame(scriptDomainData);
            return;
        }

        this.mainElement.classList.add("sdkPopup");
        this.setSdkBodyElement(
            '<div id="sdkFormCloseButton" onclick="QoreIDWebSdk.closeVerification()">x</div>'
        );
        this.setSdkStatusElement("Please wait....");
        let dots = "..";
        while (typeof ejs === "undefined") {
            dots = dots.length >= 5 ? (dots = "..") : dots + ".";
            this.setSdkStatusElement("Please wait" + dots);
            await new Promise((resolve) => setTimeout(resolve, 500));
        }
        this.sdkInitializationData = await this.getSdkInitializationData(
            this.sdkElementData.clientId,
            this.sdkElementData.flowId || 0
        );
        if (this.sdkInitializationData?.token?.accessToken) {
            const settingColorsStyle = document.createElement("style");
            Config.qoreIdPrimaryColor =
                this.sdkInitializationData?.sdkSettings?.primaryColor ||
                "#3374EF";
            settingColorsStyle.textContent = `
                #QoreIDButton #sdkRequestBodyHolder .sdkButton {
                    background-color: ${Config.qoreIdPrimaryColor} !important;
                    color: auto;
                    border-color: ${Config.qoreIdPrimaryColor} !important;
                }
                #QoreIDButton #sdkRequestBodyHolder .sdkButton.sdkButtonInline {
                    background-color: ${Config.qoreIdPrimaryColor} !important;
                    color: auto;
                    border-color: ${Config.qoreIdPrimaryColor} !important;
                }

                #QoreIDButton #sdkRequestBodyHolder #sdkRequestLoader {
                    border-top: 5px solid ${Config.qoreIdPrimaryColor};
                }
                #QoreIDButton #sdkRequestBodyHolder .sdkStepCounter {
                    border-top: 5px solid ${Config.qoreIdPrimaryColor} !important;
                }

                #QoreIDButton #sdkRequestBodyHolder .sdkProductDescription {
                    color: ${Config.qoreIdPrimaryColor} !important;
                }

                #QoreIDButton #sdkRequestBodyHolder .sdkSubForm .sdkSubFormTitle {
                    color: ${Config.qoreIdPrimaryColor} !important;
                }
            `;
            this.mainElement.appendChild(settingColorsStyle);
            document.getElementById("sdkPoweredByText")?.style.setProperty('display', `${this.allowedWhiteLabel ? "none" : "block"
                }`)
            const successfullyInitialized =
                (await this.startVerification()) || false;
            if (successfullyInitialized) this.setSdkStatusElement("");
        }
    }

    async startVerification(): Promise<boolean> {
        throw new Error("method not implemented.");
    }

    reformatCodeForDisplay(code: string, displayName: string) {
        const livenessPadding = code.includes("liveness") ? " Liveness " : "";
        const fvPadding = code.includes("face_verification") ? "Face Verification " : "";
        const newCode: string = fvPadding + code.replace('verify_', '').replace("_", " ").replace("sub", "").replace("premium", "").replace("basic", "").replace("liveness", "").replace("faceverification", "") + livenessPadding + " Verification";

        const IDs: string[] = ["bvn", "nin", "cac", "ocr", "vin", "tin", "brs", "nuban"]
        const firstWord = newCode.split(" ")[0];
        const finalWord = `${(IDs.includes(firstWord)) ? firstWord.toUpperCase() : firstWord} ${newCode.replace(firstWord, " ").replace("Drivers", "Driver's")}`;
        return code.includes("verify_face_verification") ? displayName : finalWord.replace("nin", "NIN").replace("bvn", "BVN").replace("ng", " ").replace("v2", "").replace("ocr", "OCR").replace("slip", "").replace("boolean", "").replace("individual", "physical");
    }

    async renderSdkRequestBody(sdkVerificationData: SdkProduct) {
        // sort the fields
        if (sdkVerificationData.fields?.length)
            sdkVerificationData.fields.sort((a, b) => a.priority - b.priority);
        if (sdkVerificationData.applicantFields?.length) {

            const enforceApplicantData = !(this.controllerType === 'collection'
                && this.sdkInitializationData.metadata.enabledOpenIdentityApi === true
                && !(this.sdkElementData.productCode?.includes('liveness') ||
                    this.sdkElementData.productCode?.includes('face_') ||
                    this.sdkElementData.productCode?.includes('ocr')))

            sdkVerificationData.applicantFields?.forEach((eachField) => {
                (eachField as any).isOptional = eachField.isOptional || !enforceApplicantData;
            })

            sdkVerificationData.applicantFields.sort(
                (a, b) => a.priority - b.priority
            );
        }

        // TODO - Populate the default fields value
        Object.keys(this.sdkElementData.identityData || {}).forEach((k) => {
            if (
                typeof (this.sdkElementData.identityData as any)[k] !==
                "undefined"
            ) {
                sdkVerificationData.fields?.forEach((eachField) => {
                    if (eachField.code === k && !eachField.defaultValue) {
                        eachField.defaultValue = (
                            this.sdkElementData.identityData as any
                        )[k];
                    }
                });

                sdkVerificationData.applicantFields?.forEach((eachField) => {
                    (eachField as any).isOptional = eachField.isOptional
                    if (eachField.code === k && !eachField.defaultValue) {
                        eachField.defaultValue = (
                            this.sdkElementData.identityData as any
                        )[k];
                    }
                });
            }
        });
        Object.keys(this.sdkElementData.applicantData || {}).forEach((k) => {
            if (
                typeof (this.sdkElementData.applicantData as any)[k] !==
                "undefined"
            ) {
                sdkVerificationData?.applicantFields?.forEach((eachField) => {
                    if (eachField.code === k && !eachField.defaultValue) {
                        eachField.defaultValue = (
                            this.sdkElementData.applicantData as any
                        )[k];
                    }
                });
            }
        });

        // Set the icon based on sdkInitializationData or use the image property
        sdkVerificationData.icon = this.sdkInitializationData.sdkSettings.hasLogo
            ? "data:image/png;base64, " + this.sdkInitializationData.sdkSettings.logo
            : sdkVerificationData.icon ? sdkVerificationData.icon : sdkVerificationData.image;
        const displayApplicantForm: boolean = this.sdkInitializationData.sdkSettings.displayApplicantForm;
        let isApplicantFieldMissing: boolean = false;
        let isPhoneNumberInInternationalFormat: boolean = true;
        let missingFields: string[] = [];
        
        if(typeof sdkVerificationData.applicantFields !== "undefined") {
            for(let i = 0; i<sdkVerificationData?.applicantFields.length; i++) {
                if(sdkVerificationData.applicantFields[i].code === "phone" && sdkVerificationData.applicantFields[i]?.defaultValue && !sdkVerificationData.applicantFields[i]?.defaultValue.includes("+")) isPhoneNumberInInternationalFormat = false;
                if((typeof sdkVerificationData.applicantFields[i]?.defaultValue === "undefined") && !sdkVerificationData.applicantFields[i].isOptional) {
                    isApplicantFieldMissing = true;
                    missingFields.push(sdkVerificationData.applicantFields[i].name);
                }
            }
        }

        // render the screen
        let description = sdkVerificationData?.formFieldTitle as string;
        const countryPhoneCodes = PHONE_CODES;
        const formattedDisplayName = sdkVerificationData.displayName;

        const htmlString = await this.getEjs()
            .render(sdkRequestBodyEjs, { ...sdkVerificationData, description, countryPhoneCodes, formattedDisplayName, displayApplicantForm, isApplicantFieldMissing }, { async: true })
            .catch((e) => {
                console.error(e);
                return "<h3>Error loading request data</h3>";
            });
            if(isApplicantFieldMissing && !displayApplicantForm) {
                this.setSdkBodyElement(`
                    <div id="sdkFormCloseButton" class="sdkErrorCloseButton" onclick="QoreIDWebSdk.closeVerification()">x</div>
                    <p class="sdkErrorMessage">Applicant data is required (${missingFields.toString()}) </p>
                `);
            } else if(!isPhoneNumberInInternationalFormat) {
                this.setSdkBodyElement(`
                    <div id="sdkErrorCloseButton" class="sdkErrorCloseButton" onclick="QoreIDWebSdk.closeVerification()">x</div>
                    <p class="sdkErrorMessage">invalid applicant phone number. Ensure international phone number format starting with “+“</p>
                `);
            } else {
                this.setSdkBodyElement(htmlString);
            }
    }

    getVideo() {
        this.video = document.getElementById('sdkVideo') as HTMLVideoElement;
        navigator.mediaDevices.getUserMedia(this.constraints)
            .then(stream => {
                this.video.srcObject = stream;
                this.video.play();
            })
            .catch(err => console.error('Error:', err));
    }

    async captureImageFromCameraOnly(element: HTMLDivElement) {
        this.activeElement = element;
        const modal = document.getElementById("skdModal") as HTMLDivElement;
        // camera capturing
        modal.style.display = "block";

        this.getVideo();
    }

    takePhoto() {
        // Simulate image capture and show it
        const modalImg = document.getElementById('sdkModalImg') as HTMLImageElement;
        this.takePhotoBtn = document.getElementById('sdkTakePhotoBtn') as HTMLButtonElement;
        this.actionBtns = document.getElementById('sdkActionButtons') as HTMLDivElement;

        modalImg.style.display = 'block'; // Show captured image

        const canvas = document.createElement('canvas') as HTMLCanvasElement;
        canvas.width = 320;
        canvas.height = 450;
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
        ctx.drawImage(this.video, 0, 0, 320, 450);
        const data = canvas.toDataURL('image/jpeg');

        modalImg.src = data;
        modalImg.style.display = 'block';
        this.video.style.display = 'none';

        // Hide the "Take Photo" button and show the "Retake" and "Confirm" buttons
        this.takePhotoBtn.style.display = 'none';
        this.actionBtns.style.display = 'flex';
    }

    retakePhoto() {
        // Hide the captured image and show the "Take Photo" button again
        const modalImg = document.getElementById('sdkModalImg') as HTMLImageElement;

        this.video.style.display = 'block';

        this.getVideo();

        modalImg.style.display = 'none';
        this.takePhotoBtn.style.display = 'block';
        this.actionBtns.style.display = 'none';
    }

    confirmButton() {
        const modalImg = document.getElementById('sdkModalImg') as HTMLImageElement;

        if (this.activeElement) {
            const textarea = this.activeElement.querySelector('textarea') as HTMLTextAreaElement;
            const imageCard = this.activeElement.querySelector('#sdkImageCard') as HTMLDivElement;
            const capturedImage = this.activeElement.querySelector('#sdkCapturedImage') as HTMLImageElement;
            const sdkCaptureBox = this.activeElement.querySelector('#sdkCaptureBox') as HTMLDivElement;

            // Set the image source to the captured image
            capturedImage.src = modalImg.src;
            textarea.value = modalImg.src;
            sdkCaptureBox.style.display = 'none';
            imageCard.style.display = 'block';
        }

        this.closeModal()
    }

    closeModal() {
        const modal = document.getElementById("skdModal") as HTMLDivElement;
        modal.style.display = "none";

        // Reset modal state
        const video = document.getElementById('sdkVideo') as HTMLVideoElement;
        video.style.display = 'block'; // Show video again for retake

        const modalImg = document.getElementById('sdkModalImg') as HTMLDivElement;
        modalImg.style.display = 'none';

        this.takePhotoBtn.style.display = 'block';
        this.actionBtns.style.display = 'none';
    }

    closeImageCard() {
        const modal = document.getElementById("skdModal") as HTMLDivElement;
        const video = document.getElementById('sdkVideo') as HTMLVideoElement;
        const backBtn = document.getElementById("backBtn") as HTMLButtonElement;
        const modalImg = document.getElementById('sdkModalImg') as HTMLImageElement;

        backBtn.style.display = 'none';
        modal.style.display = "block";

        if (this.activeElement) {
            const capturedImage = this.activeElement.querySelector('#sdkCapturedImage') as HTMLImageElement;

            // Set the image source to the captured image
            modalImg.src = capturedImage.src;
        }
        // Reset modal state
        video.style.display = 'none'; // Show video again for retake

        modalImg.style.display = 'block';
        this.takePhotoBtn.style.display = 'none';
        this.actionBtns.style.display = 'flex';
    }

    async populateSubFieldsFor(element: HTMLSelectElement) {
        const eletmentId = element.id;
        const code = element.getAttribute("data-element-code");
        const selectValue = element.value;

        let subs = window.document.querySelectorAll(
            `[data-controller-element-id="${eletmentId}"]`
        );

        if (element.id === 'fields_vehicleMake') {
            subs = window.document.querySelectorAll(
                `[id="fields_vehicleModel"]`
            );
        }

        if (subs.length) {
            for (const sub of Array.from(subs)) {
                const subElement = sub as HTMLSelectElement;
                subElement.innerHTML = '<option value=""></option>';
                subElement.setAttribute("value", "");
                let subElementUrl = subElement.getAttribute(
                    "data-controller-element-url"
                );
                subElementUrl = String(subElementUrl || '').replace(
                    `{{${code}}}`,
                    selectValue
                );

                let options: OptionDataOptions[] = []
                if (subElementUrl) {
                    await this.apiRequest<OptionDataOptions[]>(
                        `${Config.ApiBaseUrl}${subElementUrl}`,
                        "GET",
                        {},
                        true,
                        this.sdkInitializationData?.token?.accessToken
                    )
                        .then((response) => {
                            if (response.length) options = [...response];
                        })
                        .catch((error) => {
                            console.error(
                                `Error loading "${subElementUrl}" -- ${error.message}`
                            );
                        });
                }
                else if (subElement.id === 'fields_vehicleModel') {
                    const vehicleModels = this.flowRequest.step.products[0].fields.find(f => f.code === 'vehicleModel')?.optionData
                        .options?.filter(o => o.dependsOn?.find(d => d.fieldCode === 'vehicleMake' && d.fieldValue === selectValue));
                    if (vehicleModels?.length) options = [...vehicleModels];
                }

                if (options.length) {
                    options.forEach((o) => {
                        subElement.appendChild(
                            new Option(
                                o.label,
                                o.value
                            )
                        );
                    });
                }
            };
        }
    }

    validSdkInputField(
        element: HTMLInputElement,
        isOptional: boolean,
        errorMessage: string,
        pattern?: string
    ) {
        if (!element.required || (element.value === "" && isOptional)) {
            element.setCustomValidity("");
            element.classList.remove("sdkFieldError");
        } else if (pattern && element.value !== "") {
            const valid = new RegExp(pattern).test(element.value);
            if (valid) {
                element.setCustomValidity("");
                element.classList.remove("sdkFieldError");
            } else {
                element.setCustomValidity(errorMessage);
                element.classList.add("sdkFieldError");
            }
        }
    }

    elementExtraChecker(element: HTMLElement) {
        if (element.id == "fields_currentlyEmployed") {
            const fieldHolder = document.getElementById(
                "sdkFieldHolder_endDate"
            ) as HTMLDivElement;
            const disabled = (element as HTMLInputElement).value == "yes";
            const endDateElement = document.getElementById(
                "fields_endDate"
            ) as HTMLInputElement;
            const fieldLabelElement = document.getElementById(
                "sdkFieldLabel_endDate"
            ) as HTMLInputElement;
            endDateElement.disabled = disabled;
            endDateElement.required = !disabled;
            fieldHolder.style.display = disabled ? "none" : "block";
            fieldLabelElement.textContent =
                fieldLabelElement.getAttribute("data-title") +
                (endDateElement.required ? "* :" : " :");
        } else if (element.id == "fields_businessType") {
            // const fieldHolder = document.getElementById('sdkFieldHolder_rcNumber') as HTMLDivElement;
            const isFormal = (element as HTMLInputElement).value == "Formal";
            const rcNumberElement = document.getElementById(
                "fields_rcNumber"
            ) as HTMLInputElement;
            const fieldLabelElement = document.getElementById(
                "sdkFieldLabel_rcNumber"
            ) as HTMLInputElement;
            // rcNumberElement.disabled = !isFormal;
            rcNumberElement.required = isFormal;
            // fieldHolder.style.display = !isFormal? 'none' : 'block';
            fieldLabelElement.textContent =
                fieldLabelElement.getAttribute("data-title") +
                (rcNumberElement.required ? "* :" : " :");
        }
    }

    setPhoneCountryCode(element: HTMLSelectElement) {
        const phoneFieldId = element.getAttribute("data-phoneFieldId")!
        const phoneField = document.getElementById(phoneFieldId)! as HTMLInputElement
        phoneField.value = element.value;
        phoneField.focus()
    }

    triggerTopMainElementEvent(customEvent: Event) {
        if (this.inIFrame) {
            window.top?.postMessage(
                JSON.stringify({
                    type: customEvent.type,
                    detail: (customEvent as CustomEvent).detail
                }),
                "*"
            );
        } else {
            this.mainElement.dispatchEvent(customEvent);
        }
    }

    topWindowMessageHandler(message: any) {
        let browserEventData: BrowserEventData = null as any;
        try {
            browserEventData = JSON.parse(message?.data || "");
        } catch (_) { }

        const self: BaseController = (window as WindowWithQoreIdSdk)
            .QoreIDWebSdk;

        switch (browserEventData?.type) {
            case "qoreid:verificationInitialized":
                self.sdkElementData.initializedEventTrigger(
                    new CustomEvent(browserEventData.type, {
                        detail: browserEventData.detail
                    })
                );
                break;
            case "qoreid:verificationSubmitted":
                self.sdkElementData.submittedEventTrigger(
                    new CustomEvent(browserEventData.type, {
                        detail: browserEventData.detail
                    })
                );
                break;
            case "qoreid:verificationError":
                self.sdkElementData.errorEventTrigger(
                    new CustomEvent(browserEventData.type, {
                        detail: browserEventData.detail
                    })
                );
                break;
            case "qoreid:verificationClosed":
                self.closeVerification();
                break;
            default:
                break;
        }
    }

    closeVerification() {
        const qoreIdSdkPostedData: IQoreIdButton = (window as any)
            .QoreIdSdkPostedData;

        !qoreIdSdkPostedData && this.prepareStartButton(false);
        if (this.isHybridOrchestrated || qoreIdSdkPostedData)
            this.setSdkBodyElement("");
        this.mainElement.classList.remove("sdkPopup", "sdkIFrameHolder");
        const customEvent = new CustomEvent("qoreid:verificationClosed");
        this.sdkElementData.closedEventTrigger(customEvent);
        this.triggerTopMainElementEvent(customEvent);
    }

    async initializeFaceTec() {
        // SampleAppUtilities.formatUIForDevice();

        // Set a the directory path for other FaceTec Browser SDK Resources.
        FaceTecSDK.setResourceDirectory(
            (window as any).qoreIdSdkBaseUrl +
            "/core-sdk/FaceTecSDK.js/resources"
        );

        // Set the directory path for required FaceTec Browser SDK images.
        FaceTecSDK.setImagesDirectory(
            (window as any).qoreIdSdkBaseUrl + "/core-sdk/FaceTec_images"
        );

        // Set the custom colors and icon
        FaceTecSDK.setCustomization(
            Config.retrieveConfigurationWizardCustomization(FaceTecSDK)
        );

        // Set your FaceTec Device SDK Customizations.
        // ThemeHelpers.setAppTheme(ThemeHelpers.getCurrentTheme());

        return new Promise((resolve) => {
            if (this.faceTecSDKInitialized)
                return resolve(this.faceTecSDKInitialized);
            // Initialize FaceTec Browser SDK and configure the UI features.
            Config.initializeFromAutogeneratedConfig(
                FaceTecSDK,
                (initializedSuccessfully: boolean) => {
                    window.QoreIDFaceTecSDKInitialized =
                        initializedSuccessfully;
                    this.faceTecSDKInitialized = initializedSuccessfully;
                    console.log("Initialized..... " + initializedSuccessfully);
                    if (this.faceTecSDKInitialized) {
                        // SampleAppUtilities.enableControlButtons();

                        // Set the sound files that are to be used for Vocal Guidance.
                        // SampleAppUtilities.setVocalGuidanceSoundFiles();

                        // Set the strings to be used for group names, field names, and placeholder texts for the FaceTec ID Scan User OCR Confirmation Screen.
                        // SampleAppUtilities.setOCRLocalization();

                        // AdditionalScreens.setServerUpgradeStyling(document.getElementById("controls")!, self.exitAdditionalScreen);
                        console.warn(
                            "initialized biometrics scanner successfully...... ;)"
                        );
                    } else {
                        console.warn(
                            "initializing biometrics scanner failed...... :("
                        );
                    }
                    //   SampleAppUtilities.displayStatus(FaceTecSDK.getFriendlyDescriptionForFaceTecSDKStatus(FaceTecSDK.getStatus()));
                    console.log(
                        FaceTecSDK.getFriendlyDescriptionForFaceTecSDKStatus(
                            FaceTecSDK.getStatus()
                        )
                    );
                    return resolve(this.faceTecSDKInitialized);
                },
                this.sdkInitializationData.faceTecCredentials
            );
        });
    }

    async showFaceTecInstructions(productCode: string): Promise<void> {
        try {
            let currentIndex = 0;
            const instructions = [
                {
                    title: "Position Your Face Correctly",
                    content: "Center your face inside the guide. Ensure your face fits within the frame.",
                    button: "Next",
                    step: 0,
                    image_one: "/right_position.png",
                    image_two: "/wrong_position.png",
                    image_width: "142",
                    alt: 'Image Description'
                },
                {
                    title: "Ensure Good Lighting",
                    content: "Make sure your face is well-lit. Avoid shadows or bright backlights.",
                    button: "Next",
                    step: 1,
                    image_one: "/right_light.png",
                    image_two: "/wrong_light.png",
                    image_width: "240",
                    alt: 'Image Description'
                },
                {
                    title: "Hold Your Phone at Eye Level",
                    content: "Keep your phone steady at eye level. Avoid tilting up or down.",
                    button: "Next",
                    step: 2,
                    image_one: "/right_level.png",
                    image_two: "/wrong_level.png",
                    image_width: "240",
                    alt: 'Image Description'
                },
                {
                    title: "Stay Still and Follow the Prompt",
                    content: "Keep your head inside the frame and stay still until scanning is complete.",
                    button: "Next",
                    step: 3,
                    image_one: "/right_follow.png",
                    image_two: "/wrong_follow.png",
                    image_width: "240",
                    alt: 'Image Description'
                },
            ];

            const htmlString = await this.getEjs().render(sdkFaceTecInstructions, { instructions }, { async: true });
            this.setSdkBodyElement(htmlString);
            this.toggleWaiterElement(false);



            // Get all carousel elements
            const carouselItems = document.querySelectorAll('.carousel-slide');
            const prevBtn = document.querySelector('.prev-btn') as HTMLButtonElement;
            const nextBtn = document.querySelector('.next-btn') as HTMLButtonElement;
            const carouselButtons = document.querySelector('.sdkButton');
            const totalItems = instructions.length;

            // Function to update carousel state
            const updateCarousel = () => {
                // Update active slide
                carouselItems.forEach((item, index) => {
                    item.classList.toggle('active', index === currentIndex);
                });

                // Update navigation buttons
                prevBtn.disabled = currentIndex === 0;
                nextBtn.disabled = currentIndex === totalItems - 1;
            };

            // Navigation handlers
            prevBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (currentIndex > 0) {
                    currentIndex--;
                    updateCarousel();
                }
            });

            nextBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (currentIndex < totalItems - 1) {
                    currentIndex++;
                    updateCarousel();
                }
            });

            // Initialize state
            updateCarousel();

            // Update action button text
            if (carouselButtons) {
                carouselButtons.addEventListener('click', (e) => {
                    e.preventDefault();
                    console.log("Clicked carousel button");
                    this.launchFaceTec(productCode);
                });
            }
        } catch (error) {
            console.error(error);
            throw error; // Re-throw the error to be caught by the caller.
        }
    }

    async launchFaceTec(productCode: string, ocrOnly = false) {
        this.currentProductCode = productCode;
        const initialized = await this.initializeFaceTec();
        if (!initialized) {
            this.setSdkStatusElement(
                `<div class="sdkErrorMessage">Unable to launch the biometrics component</div>`
            );
            console.error("Unable to launch the biometrics view");
            this.updateFinalSubmitButton();
            return;
        }

        const sessionToken = await this.getSessionToken();
        this.latestEnrollmentIdentifier = `qoreid_collection_${this.sdkElementData.productCode
            }_${this.sdkElementData.customerReference
            }_${SampleAppUtilities.generateUUId()}`;
        if (ocrOnly) {
            return this.launchPhotoIDScan(sessionToken);
        } else if (this.productCodeHasOcr(productCode)) {
            // => either PhotoIDScan or PhotoIDMatch
            if (
                this.productCodeHasLiveness(productCode) ||
                this.productCodeHasFaceMatch(productCode)
            ) {
                return this.launchPhotoIDMatch(sessionToken);
            } else {
                return this.launchPhotoIDScan(sessionToken);
            }
        } else if (
            this.productCodeHasLiveness(productCode) ||
            this.productCodeHasFaceMatch(productCode)
        ) {
            return this.launchLivenessCheck(sessionToken);
        }

        return null;
    }

    shouldLaunchCamera(productCode: string) {
        return this.productCodeHasOcr(productCode) ||
            this.productCodeHasLiveness(productCode) ||
            this.productCodeHasFaceMatch(productCode)
    }

    // Get the Session Token from the server
    getSessionToken(sessionId = ""): Promise<string> {
        return new Promise((resolve, reject) => {
            let sessionToken = "";

            // Only handle session token error once
            let sessionTokenErrorHasBeenHandled = false;
            let XHR = new XMLHttpRequest();
            XHR.open("POST", Config.BaseURL + "/session-token");
            XHR.setRequestHeader("Content-Type", "application/json");
            XHR.setRequestHeader(
                "Authorization",
                "Bearer " + this.sdkInitializationData.token.accessToken
            );
            XHR.onreadystatechange = () => {
                if (XHR.readyState === XMLHttpRequest.DONE) {
                    try {
                        // Attempt to get the sessionToken from the response object.
                        sessionToken = JSON.parse(
                            XHR.responseText
                        ).sessionToken;
                        // Something went wrong in parsing the response. Return an error.
                        if (typeof sessionToken !== "string") {
                            if (sessionTokenErrorHasBeenHandled === false) {
                                sessionTokenErrorHasBeenHandled = true;
                                if (
                                    this.isNetworkResponseServerIsOffline(
                                        XHR.status
                                    )
                                ) {
                                    this.showAdditionalScreensServerIsDown();
                                } else {
                                    this.onServerSessionTokenError();
                                }
                            }
                            return reject(
                                "session error: unable to create session"
                            );
                        }
                    } catch {
                        // Something went wrong in parsing the response. Return an error.
                        if (sessionTokenErrorHasBeenHandled === false) {
                            sessionTokenErrorHasBeenHandled = true;
                            if (
                                this.isNetworkResponseServerIsOffline(
                                    XHR.status
                                )
                            ) {
                                this.showAdditionalScreensServerIsDown();
                            } else {
                                this.onServerSessionTokenError();
                            }
                        }
                        return reject("session error: network or server error");
                    }
                    SampleAppUtilities.hideLoadingSessionToken();
                    resolve(sessionToken);
                }
            };

            // Wait 3s, if the request is not completed yet, show the session token loading screen
            window.setTimeout(() => {
                if (XHR.readyState !== XMLHttpRequest.DONE) {
                    SampleAppUtilities.showLoadingSessionToken();
                }
            }, 3000);

            XHR.onerror = () => {
                // Something went wrong in the XHR request
                if (sessionTokenErrorHasBeenHandled === false) {
                    sessionTokenErrorHasBeenHandled = true;
                    if (this.isNetworkResponseServerIsOffline(XHR.status)) {
                        this.showAdditionalScreensServerIsDown();
                    } else {
                        this.onServerSessionTokenError();
                    }
                }
                return reject("session error: network or server error");
            };
            // sessionId = ""
            XHR.send(
                JSON.stringify({
                    apiUserAgentString:
                        FaceTecSDK.createFaceTecAPIUserAgentString(sessionId)
                })
            );
        });
    }

    // Check for server down status
    isNetworkResponseServerIsOffline(networkResponseStatus: number): boolean {
        return networkResponseStatus >= 500;
    }

    showAdditionalScreensServerIsDown() {
        AdditionalScreens.showServerUpGradeView();
    }

    onServerSessionTokenError() {
        SampleAppUtilities.handleErrorGettingServerSessionToken();
    }

    productCodeHasOcr(productCode: string) {
        return productCode.indexOf("ocr") >= 0;
    }

    productCodeHasLiveness(productCode: string) {
        // if Liveness or Re-authentication
        return productCode.indexOf("liveness") >= 0 || productCode === 'authentication';
    }

    productCodeHasFaceMatch(productCode: string) {
        return productCode.indexOf(PROD_KEYWORD_FACE_VERIFICATION) === 0;
    }

    updateFinalSubmitButton(title = "Continue", disable = false) {
        if (this.finalSubmitButton) {
            this.finalSubmitButton.innerText = title || "Continue";
            this.toggleWaiterElement(disable);
            this.finalSubmitButton.disabled = !!disable;
        }
    }

    toggleWaiterElement(show = true) {
        if (!show) {
            this.sdkBodyElement.style.display = "flex";
            this.sdkWaiterElement.style.display = "none";
        } else {
            this.sdkBodyElement.style.display = "none";
            this.sdkWaiterElement.style.display = "flex";
        }
    }

    onComplete(
        _sessionResult?: FaceTecSessionResult,
        _idScanResult?: FaceTecIDScanResult,
        _latestNetworkResponseStatus?: number,
        _latestIDScanOCRResult?: OCRScanData
    ) {
        throw new Error("method not implemented");
    }

    async submitVerification(_?: HTMLFormElement) {
        throw new Error("method not implemented");
    }

    async launchLivenessCheck(sessionToken: string) {
        new CollectionProcessor(sessionToken, this as any).launchLiveness();
    }

    async launchPhotoIDMatch(sessionToken: string) {
        new CollectionProcessor(sessionToken, this as any).launchLiveness(true);
    }

    async launchPhotoIDScan(sessionToken: string) {
        new CollectionProcessor(sessionToken, this as any).launchIDScanOnly();
    }
}

export const sdkSuccessIconImage =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGUAAABkCAYAAACfIP5qAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAYlSURBVHgB7Z2/bxxFFMe/s3eOHBSQT6JKwwESDYXPNpHoYhdQIexUlDFNoMOJDZS2S8AWpoM0cSpKHEQFRQ6JgsJO7D8AcRVpgLUgUgy+m+G99Z3lnI3tmZ3Znb2djxTFltfW7X72vTe/dhYIBAKBQCAQCAQCgUAgEAhYQKAgTM7FI48raMgoGo2UrCuFEfr0dUX/njpQYZdOapd+tisUWlJErUjKnUsdbDfXarsoAN5K6UmgTzgtBWboAteREgVsC4FtSNzbWq1twFO8k/LafDxJF+86fbIZ+nYE7uBo2hASdzdXa014hBdSulExR6noA7gVcTICLYjK0tanz92FB+QqJXcZ/ZCcbuQsIUdykzIxH88gwuewUCusk3PkZC7l9Y/j+n4Hd+jLSfiOwPpQhOWfP6m1kCERMuTKrXiWhDxEEYQwCrP7EvcnPvrrOjIks0ihdLWGg9pRSKhJvpxVrXEuhYv531V8g6JExylwP+dCBddcpzOnUpL6QeHvZTE3hRoBVGemXIpxJmUghfRwLMaJlIEW0sOhGOtSkhoyRC2sQRbShWuMbGNq2/JAp/UmMQnxs0PoALqjG5UKna9lrEqhwcQlbtujTAjMji/Ec7CItfTV7an/ipJC/ZgpW6PN1iIlKewlRkW406B6CgtYkdJNW3WUGTr/qGpnxCJ1+ip72upnqIIX0zaTU0cKpa1FBA7pjoCnIlWkJFO3oty15CTSFv1UkULFLdMh7aJAN2qq7GEcKaGWnE6aaDGOlFBLTkcKTMOQNOlrEoH/hVLQrGm/xUhKsuih7P2Ssxmp8mJCA8wiJTIPzTKRLCo0wEyKSlYvBs5CmF0nbSncN4EPC+dS8OxFgfm3L2LxnWeSrx0y0r1eWlShiYwwSs29wsISvnr/El65XEm+3/qlje82/4Ur+HrRf02d39GOFFHgVle/kN/+lIkUlwipX+y1paiCtrpOEvLel4/xKJZwiTK4iU0ixaiZlyd5CWGE0L+JtaQ0aGgFBSNPIT3G5p+8oHO8lpRqu1ipywchjIr2ajrHZ7rAO0t8EcJUFdxFiow6dWTAxMtVTLyk3Vo/xCchTPLQrAbmZ+4IFsIXlLn9/R5u/7Cn8+veCTHB6/R1481h3Hhj+NzHD4IQxjsp3Jk7Gh3nFTMoQhgtKUJWMtkcoD9tnSXGdyE01NLSOV5LSqT5x9NwXjGDFCE9tKTsR8h0G42zxBRGiBxu6RyuPW49sRBnPkbcL4Nlff3TP4WJkK2VmtZ11m4SK94AIONBSZbA9MSwpLeuXMDl2kGg+yyEn2GBJiYDkk3kQH8qK4IQRgj9Oqw/dG9g3hb9YopQ1JXBTaydviKJHZXjji4s5hHJuPrqEFa/feJ9K8vkJja6vFTsYxR8nj4jdqnIa40QM2Y9egFvNzDzCRqINLpORlJ4+yUEziQSGUppd5I8WYj9GPOCoqS1uVK7BwOMpPBz41TA1hE4jSYMMR4ljhSM7oKyIDtYhiHGUrrPXjQROAalrnXKJi0Ykmo+hffAQuAYKkqX2lNJCdFyHI6Sh5/VfkQKUs88dtp4F4FD0tSSHqmlcO6kltgSAjykspSmlvSwMkcv2/iC2+UoMXz+D1ZqVmqsFSncb6E/VOo0RmlrCpawtpqFi76S8iZKiK201cP6IPzEwh/r5Lo0mx5wa+vBas1qlrC+7qvTjubynAjLEq4jlLasZwfrUri+UOG/NuiFvyvE+v6RjLM5xMZcXI8quG/y0IzvHBHSggOcTuwOohjXQhina4n5g/MJDFCNadL5jLkUwmS2BGJ8IV4UBe750421Rp3DTJr8ma5LGb/1+yxEZbFI6Yxk8Fvw5rZWa5lNgWe+WKhbZ1jMLPynyQOurtNVP7mt4PI5ariYC3Rubq0+n8uqnRyX1R3AtYZ3/fZBjjpYDLLGA6zbOb6oM3cpPfKMHF9k9PBGSo+xD+OrQlK94begOlyF2S3gGzJKP1NoG++kHKWxEE9HvLeYQMPG9iP8GAfZ2KD/N9Q+dnyIipPwWspReD9GMYTRRI5CPUlzCiMKyYudn4oofpEz/axFF59fRcszo9s+SwgEAoFAIBAIBAKBQCAQCDjhP4Lq1G1q2pxpAAAAAElFTkSuQmCC";
export const sdkFailureIconImage =
    "data:image/png;base64,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";
